import { IntlProvider } from 'react-intl';
import { Link as RouterLink } from 'react-router-dom';
import { useLocale } from '../../../contexts/LocaleProvider';
import PageContainer from '../../productPage/tableComponents/PageContainer';
import { Box, Button, Typography } from '@mui/material';
import { useResponsive } from '../../../contexts/ResponsiveContext';
import { HomePageHeroSectionContent } from '../../../graphql/pimcore/modules/siteContent/types/HomePageContent';

type HeroProps = {
    cmsContent: HomePageHeroSectionContent;
};

export default function Hero({ cmsContent }: HeroProps) {
    const { locale, messages } = useLocale();
    const {
        isMaxScreen,
        isScreenXxl,
        isScreenXl,
        isScreenLg,
        isScreenMd,
        isScreenSm,
        isScreenXs,
        responsiveWidth,
    } = useResponsive();

    // Get hero section content
    const { pageTitle, pageSubtitle, learnMoreLink, pageTitleBackgroundImage } = cmsContent;

    const titleLines = pageTitle.content.split('\n') ?? [];
    const subtitleLines = pageSubtitle.content.split('\n') ?? [];
    const bgImageUrl = pageTitleBackgroundImage.imageUrl;

    const heroHeight = (() => {
        if (isMaxScreen) return '625px';
        if (isScreenXxl) return '625px';
        if (isScreenXl) return '625px';
        if (isScreenLg) return '439px';
        if (isScreenMd) return '386px';
        if (isScreenSm) return '221px';
        // 預設或 isScreenXs：
        return '186px';
    })();

    const backgroundGradient = (() => {
        if (isMaxScreen || isScreenXxl || isScreenXl || isScreenLg)
            return 'linear-gradient(90deg, #F2F2F2 22.79%, rgba(140, 140, 140, 0.2) 100%)';
        if (isScreenMd)
            return 'linear-gradient(181.9deg, #F2F2F2 40%, rgba(140, 140, 140, 0.2) 114.1%)';
    })();

    const containerStyles = {
        width: '100%',
        height: heroHeight,
        ...(isScreenSm || isScreenXs
            ? { background: '#F2F2F2' }
            : {
                  background: `${backgroundGradient}, url(${bgImageUrl})`,
                  backgroundSize: 'cover',
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'center',
              }),
    };

    const titleGridRow = (() => {
        if (isMaxScreen) return '3 / 7';
        if (isScreenXxl) return '3 / 7';
        if (isScreenXl) return '3 / 7';
        if (isScreenLg) return '3 / 5';
        if (isScreenMd) return '2 / 4';
        if (isScreenSm) return '1 / 3';
        // 預設或 isScreenXs：
        return '1 / 3';
    })();

    const subTitleGridRow = (() => {
        if (isMaxScreen || isScreenXxl || isScreenXl || isScreenLg) return '7 / 10';
        if (isScreenMd) return '5 / 7';
        if (isScreenSm) return '4 / 6';
        // 預設或 isScreenXs：
        return '4 / 6';
    })();

    const buttonGridRow = (() => {
        if (isMaxScreen || isScreenXxl || isScreenXl || isScreenLg) return '10 / 11';
        if (isScreenMd) return '8 / 9';
        if (isScreenSm) return '11 / 12';
        // 預設或 isScreenXs：
        return '12 / 12';
    })();

    const useMobileProductDisplay = isScreenMd || isScreenSm || isScreenXs;

    const heroTitleTypography = (() => {
        if (isMaxScreen || isScreenXxl || isScreenXl || isScreenLg) {
            return {
                fontFamily: 'Poppins',
                fontWeight: 700,
                fontSize: '85px',
                lineHeight: '119%',
                letterSpacing: '0%',
                color: '#000',
            };
        } else if (isScreenMd) {
            return {
                fontFamily: 'Poppins',
                fontWeight: 700,
                fontSize: '63px',
                lineHeight: '109%',
                letterSpacing: '0%',
                color: '#000',
            };
        } else if (isScreenSm) {
            return {
                fontFamily: 'Poppins',
                fontWeight: 700,
                fontSize: '40px',
                lineHeight: '119%',
                letterSpacing: '0%',
                color: '#000',
            };
        } else {
            return {
                fontFamily: 'Poppins',
                fontWeight: 700,
                fontSize: '28px',
                lineHeight: '119%',
                letterSpacing: '0%',
                color: '#000',
            };
        }
    })();

    const heroSubTitleTypography = (() => {
        if (isMaxScreen || isScreenXxl || isScreenXl || isScreenLg || isScreenMd || isScreenSm) {
            return {
                fontFamily: 'Poppins',
                fontWeight: 500,
                fontSize: '24px',
                lineHeight: '117%',
                letterSpacing: '0%',
                color: '#7A7C81',
            };
        } else {
            return {
                fontFamily: 'Poppins',
                fontWeight: 500,
                fontSize: '20px',
                lineHeight: '117%',
                letterSpacing: '0%',
                color: '#7A7C81',
            };
        }
    })();

    const buttonStyle = (() => {
        if (isMaxScreen || isScreenXxl || isScreenXl || isScreenLg || isScreenMd) {
            return {
                minWidth: '155px', // 保留一個最小寬度
                width: 'auto', // 讓按鈕寬度自動擴展
                height: '46px',
                gap: '10px',
                borderRadius: '5px',
                padding: '5px 11px',
                textTransform: 'none',
                fontFamily: 'Poppins',
                fontSize: '24px',
                backgroundColor: '#E5673E',
                color: 'white',
                fontWeight: 400,
                whiteSpace: 'nowrap', // 防止文字換行
                transition: 'none', // 禁用過渡動畫

                '&:hover': {
                    backgroundImage: 'linear-gradient(#ee9769, #e5673e)',
                    transition: 'none', // 禁用過渡動畫
                },
            };
        } else {
            return {
                minWidth: '136px',
                width: 'auto',
                height: '40px',
                gap: '10px',
                borderRadius: '5px',
                padding: '5px 11px 5px 11px',
                textTransform: 'none',
                fontFamily: 'Poppins',
                fontSize: '20px',
                backgroundColor: '#E5673E',
                color: 'white',
                fontWeight: 400,
                whiteSpace: 'nowrap', // 防止文字換行
                transition: 'none', // 禁用過渡動畫
                '&:hover': {
                    backgroundImage: 'linear-gradient(#ee9769, #e5673e)',
                    transition: 'none', // 禁用過渡動畫
                },
            };
        }
    })();

    return (
        <IntlProvider locale={locale} key={locale} defaultLocale="en" messages={messages}>
            <Box
                width="100%"
                data-testid="hero-section"
                sx={{
                    padding: 0,
                    flexGrow: 0,
                    p: 0,
                }}
            >
                <PageContainer
                    sx={containerStyles}
                    innerSx={{ width: responsiveWidth }}
                    containerName={'hero'}
                >
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateRows: 'repeat(12, 1fr)',
                            height: '100%',
                        }}
                    >
                        {/*主標題 */}
                        <Box
                            sx={{
                                gridRow: titleGridRow,
                                display: 'flex',
                                alignItems: 'center',
                            }}
                            data-testid={'hero-title'}
                        >
                            <Typography sx={heroTitleTypography} component="h1">
                                {titleLines.map((line, i) => (
                                    <span key={i}>
                                        {line}
                                        {i < titleLines.length - 1 &&
                                            (useMobileProductDisplay ? ' ' : <br />)}
                                    </span>
                                ))}
                            </Typography>
                        </Box>

                        {/*描述文字 */}
                        <Box
                            sx={{
                                gridRow: subTitleGridRow,
                                display: 'flex',
                                alignItems: 'flex-start',
                                flexDirection: 'column',
                                marginTop: useMobileProductDisplay ? '0' : '1rem',
                            }}
                            data-testid={'hero-subtitle'}
                        >
                            {subtitleLines.map((line, i) => (
                                <Typography key={i} sx={heroSubTitleTypography}>
                                    {line}
                                </Typography>
                            ))}
                        </Box>

                        {/* 按鈕 */}
                        <Box
                            sx={{
                                gridRow: buttonGridRow,
                                display: 'flex',
                            }}
                            data-testid="hero-subbutton"
                        >
                            {learnMoreLink && learnMoreLink.kind === 'internalLink' && (
                                <Button
                                    sx={buttonStyle}
                                    variant="contained"
                                    component={RouterLink}
                                    to={learnMoreLink.linkRoute}
                                >
                                    {learnMoreLink.linkText}
                                </Button>
                            )}
                        </Box>
                    </Box>
                </PageContainer>
            </Box>
        </IntlProvider>
    );
}
